/** @graphql cc **/
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { createSingleQueryParamHook } from '@confluence/route-manager';

import type { ProgressiveRenderingConfigQuery as ProgressiveRenderingConfigQueryType } from './__types__/ProgressiveRenderingConfigQuery';
import type { AdfStats } from './useAdfStats';

const STATSIG_DYNAMIC_CONFIG = 'cc_complexit_fe_progressive_adf_rendering_configs';

type UseProgressiveRenderingConfigResponseType = {
	isComplexPage: boolean;
	batchSize: number;
};

type UseProgressiveRenderingConfigProps = {
	skip?: boolean;
	totalNodeCount: AdfStats['totalNodeCount'];
};

// complexitNodes value example: conplexitNodes=1000,10 where 1000 is the batch size and 10 is the multiplier
const useQueryParamComplexitNodes = createSingleQueryParamHook('complexitNodes');

const ProgressiveRenderingConfigQuery = gql`
	query useProgressiveRenderingConfigQuery {
		user: confluence_atlassianUser(current: true) {
			id
			emails {
				value
			}
		}
	}
`;

export const useProgressiveRenderingConfig = ({
	skip,
	totalNodeCount,
}: UseProgressiveRenderingConfigProps): UseProgressiveRenderingConfigResponseType => {
	// For testing purposes, we allow Atlassian employees to override the default config
	const complexitNodesConfig = useQueryParamComplexitNodes()?.split(',');
	const { data } = useQuery<ProgressiveRenderingConfigQueryType>(ProgressiveRenderingConfigQuery, {
		skip:
			skip ||
			!complexitNodesConfig ||
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			Boolean(process.env.REACT_SSR),
	});

	let isComplexPage = false;
	let batchSize = 0;

	if (!skip) {
		const defaultConfig = StatsigConfigurations.getString(STATSIG_DYNAMIC_CONFIG, '').split(',');
		batchSize = Number(defaultConfig[0]) || 1000;
		let multiplier = Number(defaultConfig[1]) || 10;

		// Allow Atlassian employees to override the default config:
		const isAtlassianUser = data?.user?.emails?.some((email) =>
			email?.value?.endsWith('@atlassian.com'),
		);
		if (isAtlassianUser) {
			// Support partial value format like ,10 or 1000,
			const complexitNodesBatchSize = Number(complexitNodesConfig?.[0]);
			if (complexitNodesBatchSize > 0) {
				batchSize = complexitNodesBatchSize;
			}
			const complexitNodesMultiplier = Number(complexitNodesConfig?.[1]);
			if (complexitNodesMultiplier > 0) {
				multiplier = complexitNodesMultiplier;
			}
		}

		// Figure out whether progressive rendering is to be enabled:
		if (totalNodeCount >= batchSize * multiplier) {
			isComplexPage = true;
		}
	}

	return {
		isComplexPage,
		batchSize,
	};
};
